import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { PencilSimpleLine } from "@phosphor-icons/react";
import RatingSummary from "@/components/reviews/RatingSummary";
import ReviewCard from "@/components/reviews/ReviewCard";
import ReviewForm from "@/components/reviews/ReviewForm";
import useReviews from "@/components/reviews/useReviews";
import useReviewViewer from "@/components/reviews/useReviewViewer";
import { OPEN_REVIEW_EVENT } from "@/components/reviews/CustomerPhotos";

export default function ProductReviews({ product }) {
  const [rating, setRating] = useState(null);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const { data, items, hasMore, loadMore } = useReviews({ product_id: product.id, rating: rating || undefined }, 6);
  const { openPhoto, viewer } = useReviewViewer(items);

  useEffect(() => {
    const open = () => {
      document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(true);
    };
    window.addEventListener(OPEN_REVIEW_EVENT, open);
    return () => window.removeEventListener(OPEN_REVIEW_EVENT, open);
  }, []);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("review") !== "1") return;
    const go = () => setTimeout(() => window.dispatchEvent(new Event(OPEN_REVIEW_EVENT)), 400);
    if (sp.get("o") && sp.get("t")) {
      api.get("/reviews/prefill", { params: { o: sp.get("o"), t: sp.get("t") } })
        .then((r) => setPrefill(r.data)).catch(() => {}).finally(go);
    } else go();
  }, []);

  return (
    <section id="reviews" data-testid="product-reviews" className="mt-20 pt-12 border-t border-border">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Customer reviews</div>
          <h2 className="font-display text-3xl md:text-4xl mt-2">What the crew says</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="pdp-write-review-btn" className="btn-primary rounded-sm h-11 px-5 font-display tracking-wide self-start">
              <PencilSimpleLine size={16} className="mr-2" /> Write a review
            </Button>
          </DialogTrigger>
          {open && <ReviewForm product={product} prefill={prefill} onDone={() => setOpen(false)} />}
        </Dialog>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-10 items-start">
        <div className="lg:sticky lg:top-24"><RatingSummary data={data} active={rating} onPick={setRating} /></div>
        {items.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
            {items.map((r) => <ReviewCard key={r.id} review={r} onPhoto={openPhoto} />)}
            {hasMore && (
              <Button variant="outline" data-testid="pdp-reviews-more" onClick={loadMore} className="rounded-sm md:col-span-2 justify-self-center">
                Show more reviews
              </Button>
            )}
          </div>
        ) : (
          <div data-testid="pdp-reviews-empty" className="border border-dashed border-border rounded-sm p-10 text-center text-sm text-muted-foreground">
            No reviews yet. Be the first to tell the crew how it holds up.
          </div>
        )}
      </div>
      {viewer}
    </section>
  );
}
